/**
 * Компании: список фирм на сервере, заведение новой, нагрузка сервера в
 * целом, журнал действий над фирмами и выдача ключа фирме на её карточке.
 *
 * Экран видит только администратор сервера — тот же человек, кому открыт
 * журнал в управлении, и ровно по той же причине: Traccar отдаёт эти данные
 * лишь ему, а не любому администратору отдельной фирмы. Настоящая проверка —
 * на сервере, здесь лишь то, чтобы не показывать дверь, за которой заведомо
 * откажут.
 *
 * Вёрстка и приёмы — те же, что в Manage.tsx: список карточкой открывает
 * подробности, форма — обычный `<form>` с полями `.field`, отказ показывается
 * текстом сервера через `readable`, а не второй копией его правил, а вкладки
 * над списком — та же полоска `.chips`, что и в управлении.
 *
 * Отзыва ключа на этом экране нет: сервер не отдаёт список выданных ключей
 * фирмы (ключ виден один раз при выдаче), значит и отзывать здесь нечего —
 * `id` ключа неоткуда взять. `api.revokeCompanyKey` останется невостребованным
 * до экрана, который покажет список ключей.
 */

import { useCallback, useEffect, useState } from 'react'

import { api, type Company, type Health, type HostAction } from './api'
import { readable } from './apiText'
import { dateTime, duration } from './format'
import { dict, type Dict, type Lang } from './i18n'
import { daysLeft, termTone } from './term'

type View = { kind: 'list' } | { kind: 'new' } | { kind: 'company'; company: Company }

/**
 * Три вкладки одного экрана: список фирм, нагрузка сервера и журнал
 * действий над фирмами. Тот же приём, что и вкладки в Manage.tsx —
 * полоска `.chips` над списком, доступна только в виде `list`.
 */
type Section = 'companies' | 'load' | 'journal'

type Props = {
  lang: Lang
}

export function Companies({ lang }: Props) {
  const t = dict(lang)
  const [rows, setRows] = useState<Company[] | null>(null)
  const [view, setView] = useState<View>({ kind: 'list' })
  const [section, setSection] = useState<Section>('companies')
  const [error, setError] = useState('')

  const reload = useCallback(() => {
    api
      .listCompanies()
      .then((data) => {
        setRows(data)
        setError('')
      })
      .catch((caught) => setError(readable(caught, t)))
  }, [t])

  useEffect(() => {
    reload()
  }, [reload])

  function backToList() {
    setView({ kind: 'list' })
    setError('')
  }

  return (
    <div className="manage">
      {view.kind === 'list' && (
        <>
          <div className="chips" role="tablist">
            {(['companies', 'load', 'journal'] as const).map((key) => (
              <button
                key={key}
                role="tab"
                aria-selected={section === key}
                onClick={() => setSection(key)}
              >
                {key === 'companies' ? t.companies : key === 'load' ? t.load : t.journal}
              </button>
            ))}
          </div>

          {section === 'companies' && (
            <>
              {error && (
                <div className="login-error" role="alert">
                  {error}
                </div>
              )}

              {rows === null && <p className="hint">{t.loading}</p>}
              {rows?.length === 0 && <p className="hint">{t.companiesEmpty}</p>}

              {rows?.map((company) => (
                <button
                  key={company.id}
                  className="item no-icon"
                  onClick={() => setView({ kind: 'company', company })}
                >
                  <span>
                    <span className="i-name">
                      {company.company}
                      {company.disabled && <em className="tag">{t.mng.disabled}</em>}
                      {company.invite === 'pending' && (
                        <em className="tag">{t.invitePending}</em>
                      )}
                      {company.invite === 'unsent' && (
                        <em className="tag">{t.inviteUnsent}</em>
                      )}
                    </span>
                    <span className="i-sub">
                      {t.machinesOfLimit(
                        company.devices,
                        company.deviceLimit === -1 ? t.unlimited : String(company.deviceLimit),
                      )}
                      {' · '}
                      <b className="term" data-tone={termTone(company.expiresAt)}>
                        {termText(company.expiresAt, t)}
                      </b>
                    </span>
                  </span>
                  <span className="chev">›</span>
                </button>
              ))}

              <button className="act" onClick={() => setView({ kind: 'new' })}>
                {t.companiesNew}
              </button>
            </>
          )}

          {section === 'load' && <CompanyLoad lang={lang} t={t} />}
          {section === 'journal' && <CompanyJournal lang={lang} t={t} />}
        </>
      )}

      {view.kind === 'new' && (
        <CompanyForm
          t={t}
          onDone={() => {
            reload()
            backToList()
          }}
          onCancel={backToList}
        />
      )}

      {view.kind === 'company' && (
        <CompanyCard
          company={view.company}
          lang={lang}
          t={t}
          onDone={() => {
            reload()
            backToList()
          }}
          onCancel={backToList}
        />
      )}
    </div>
  )
}

/** Срок по-человечески: «осталось N дней», «срок истёк» или «бессрочно». */
function termText(expiresAt: string | null, t: Dict): string {
  const left = daysLeft(expiresAt)
  if (left === null) return t.termUnlimited
  if (left <= 0) return t.termExpired
  return t.termDaysLeft(left)
}

// --- нагрузка сервера --------------------------------------------------------

/**
 * Состояние сервера в целом: версия, аптайм, сколько комнат и машин и когда
 * пришла последняя позиция. Для v0.1 достаточно читаемых чисел — полоски и
 * графики оставлены на потом, как и в KEPwise-метриках, откуда взят приём.
 */
function CompanyLoad({ lang, t }: { lang: Lang; t: Dict }) {
  const [health, setHealth] = useState<Health | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api
      .companyHealth()
      .then((data) => {
        if (!cancelled) setHealth(data)
      })
      .catch((caught) => {
        if (!cancelled) setError(readable(caught, t))
      })
    return () => {
      cancelled = true
    }
  }, [t])

  if (error) {
    return (
      <div className="login-error" role="alert">
        {error}
      </div>
    )
  }
  if (health === null) return <p className="hint">{t.loading}</p>

  const uptimeMs = Math.max(Date.now() - new Date(health.startedAt).getTime(), 0)

  return (
    <div className="rows">
      <div className="kv">
        <span>{t.healthVersion}</span>
        <b>{health.version}</b>
      </div>
      <div className="kv">
        <span>{t.healthUptime}</span>
        <b>{duration(uptimeMs, lang, t)}</b>
      </div>
      <div className="kv">
        <span>{t.healthRooms}</span>
        <b>{health.rooms}</b>
      </div>
      <div className="kv">
        <span>{t.healthDevices}</span>
        <b>{health.devices}</b>
      </div>
      <div className="kv">
        <span>{t.healthOnline}</span>
        <b>{health.online}</b>
      </div>
      <div className="kv">
        <span>{t.healthLastPosition}</span>
        <b>{health.lastPositionAt ? dateTime(health.lastPositionAt, lang, true) : t.never}</b>
      </div>
    </div>
  )
}

// --- журнал действий над фирмами --------------------------------------------

/** Код действия из журнала → человекочитаемая метка. Неизвестный код — как есть. */
function journalActionLabel(action: string, t: Dict): string {
  switch (action) {
    case 'create_org':
      return t.actCreateOrg
    case 'disable':
      return t.actDisable
    case 'enable':
      return t.actEnable
    case 'set_limits':
      return t.actSetLimits
    case 'issue_key':
      return t.actIssueKey
    case 'revoke_key':
      return t.actRevokeKey
    default:
      return action
  }
}

function CompanyJournal({ lang, t }: { lang: Lang; t: Dict }) {
  const [rows, setRows] = useState<HostAction[] | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    api
      .companyJournal()
      .then((data) => {
        if (!cancelled) setRows(data)
      })
      .catch((caught) => {
        if (!cancelled) setError(readable(caught, t))
      })
    return () => {
      cancelled = true
    }
  }, [t])

  if (error) {
    return (
      <div className="login-error" role="alert">
        {error}
      </div>
    )
  }
  if (rows === null) return <p className="hint">{t.loading}</p>
  if (rows.length === 0) return <p className="hint">{t.journalEmpty}</p>

  return (
    <div className="rows">
      {rows.map((row, i) => (
        <div className="rec" key={`${row.at}-${i}`}>
          <div className="rec-top">
            <b>{journalActionLabel(row.action, t)}</b>
            <span>{dateTime(row.at, lang, true)}</span>
          </div>
          <div className="rec-sub">
            {row.actor} · {row.room}
            {row.detail ? ` · ${row.detail}` : ''}
          </div>
        </div>
      ))}
    </div>
  )
}

// --- заведение фирмы ------------------------------------------------------

function CompanyForm({
  t,
  onDone,
  onCancel,
}: {
  t: Dict
  onDone: () => void
  onCancel: () => void
}) {
  const [company, setCompany] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [deviceLimit, setDeviceLimit] = useState('')
  const [userLimit, setUserLimit] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.createCompany({
        company,
        contactEmail,
        deviceLimit: deviceLimit.trim() ? Number(deviceLimit) : -1,
        userLimit: userLimit.trim() ? Number(userLimit) : -1,
        expiresAt: expiresAt || null,
      })
      onDone()
    } catch (caught) {
      setError(readable(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <button type="button" className="back" onClick={onCancel}>
        ‹ {t.companies}
      </button>
      <h3 className="c-name">{t.companiesNew}</h3>

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      <label className="field">
        <span>{t.companyName}</span>
        <input value={company} onChange={(e) => setCompany(e.target.value)} autoFocus required />
      </label>
      <label className="field">
        <span>{t.ownerEmail}</span>
        <input
          type="email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          autoComplete="off"
          autoCapitalize="none"
          inputMode="email"
          required
        />
      </label>
      <label className="field">
        <span>{t.deviceLimit}</span>
        <input
          value={deviceLimit}
          onChange={(e) => setDeviceLimit(e.target.value)}
          inputMode="numeric"
          placeholder={t.unlimited}
        />
      </label>
      <label className="field">
        <span>{t.userLimit}</span>
        <input
          value={userLimit}
          onChange={(e) => setUserLimit(e.target.value)}
          inputMode="numeric"
          placeholder={t.unlimited}
        />
      </label>
      <label className="field">
        <span>{t.expiry}</span>
        <input
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          placeholder={t.unlimited}
        />
      </label>

      <button className="act" type="submit" disabled={busy}>
        {busy ? t.working : t.create}
      </button>
    </form>
  )
}

// --- карточка фирмы --------------------------------------------------------

function CompanyCard({
  company,
  lang,
  t,
  onDone,
  onCancel,
}: {
  company: Company
  lang: Lang
  t: Dict
  onDone: () => void
  onCancel: () => void
}) {
  const savedDeviceLimit = company.deviceLimit === -1 ? '' : String(company.deviceLimit)
  const savedUserLimit = company.userLimit === -1 ? '' : String(company.userLimit)
  const savedExpiry = company.expiresAt ? company.expiresAt.slice(0, 10) : ''

  const [deviceLimit, setDeviceLimit] = useState(savedDeviceLimit)
  const [userLimit, setUserLimit] = useState(savedUserLimit)
  const [expiresAt, setExpiresAt] = useState(savedExpiry)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  // Выдача ключа — отдельный поток: хранилище держит только хеш, сырой ключ
  // отдаётся один раз в ответе на выдачу и нигде больше не восстановим.
  // Поэтому после выдачи карточка не закрывается через onDone() — человек
  // должен успеть его скопировать, а не увидеть список фирм.
  const [issuingKey, setIssuingKey] = useState(false)
  const [keyName, setKeyName] = useState('')
  const [issuedKey, setIssuedKey] = useState<string | null>(null)
  const [keyBusy, setKeyBusy] = useState(false)

  async function issueKey(event: React.FormEvent) {
    event.preventDefault()
    setKeyBusy(true)
    setError('')
    try {
      const result = await api.issueCompanyKey(company.id, keyName)
      setIssuedKey(result.key)
      setIssuingKey(false)
      setKeyName('')
    } catch (caught) {
      setError(readable(caught, t))
    } finally {
      setKeyBusy(false)
    }
  }

  // Кнопка «Сохранить» оживает, только если что-то и правда поменяли — как
  // в карточке машины и человека в Manage.tsx.
  const changed =
    deviceLimit !== savedDeviceLimit || userLimit !== savedUserLimit || expiresAt !== savedExpiry

  async function run(action: () => Promise<unknown>) {
    setBusy(true)
    setError('')
    try {
      await action()
      onDone()
    } catch (caught) {
      setError(readable(caught, t))
    } finally {
      setBusy(false)
    }
  }

  function save() {
    void run(() =>
      api.editCompany(company.id, {
        deviceLimit: deviceLimit.trim() ? Number(deviceLimit) : -1,
        userLimit: userLimit.trim() ? Number(userLimit) : -1,
        expiresAt: expiresAt || null,
        clearExpiry: !expiresAt,
      }),
    )
  }

  return (
    <div>
      <button className="back" onClick={onCancel}>
        ‹ {t.companies}
      </button>
      <h3 className="c-name">{company.company}</h3>
      <p className="c-state">
        <em>{company.contact}</em>
      </p>

      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      <div className="rows">
        <div className="kv">
          <span>{t.people}</span>
          <b>{company.people}</b>
        </div>
        <div className="kv">
          <span>{t.lastSeen}</span>
          <b>{company.lastSeen ? dateTime(company.lastSeen, lang, true) : t.never}</b>
        </div>
        {company.invite === 'pending' && (
          <div className="kv">
            <span>{t.invitePending}</span>
          </div>
        )}
        {company.invite === 'unsent' && (
          <div className="kv">
            <span>{t.inviteUnsent}</span>
          </div>
        )}
      </div>

      <label className="field">
        <span>{t.deviceLimit}</span>
        <input
          value={deviceLimit}
          onChange={(e) => setDeviceLimit(e.target.value)}
          inputMode="numeric"
          placeholder={t.unlimited}
        />
      </label>
      <label className="field">
        <span>{t.userLimit}</span>
        <input
          value={userLimit}
          onChange={(e) => setUserLimit(e.target.value)}
          inputMode="numeric"
          placeholder={t.unlimited}
        />
      </label>
      <label className="field">
        <span>{t.expiry}</span>
        <input
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          placeholder={t.unlimited}
        />
      </label>

      <button className="act" disabled={busy || !changed} onClick={save}>
        {busy ? t.working : t.mng.save}
      </button>

      <button
        className="act ghost"
        disabled={busy}
        onClick={() =>
          void run(() =>
            company.disabled ? api.enableCompany(company.id) : api.disableCompany(company.id),
          )
        }
      >
        {busy ? t.working : company.disabled ? t.enable : t.disable}
      </button>

      {issuedKey && (
        <div className="rows">
          <div className="rec">
            <div className="rec-top">
              <b>{t.keyShownOnce}</b>
            </div>
            <div className="rec-sub">
              <code>{issuedKey}</code>
            </div>
          </div>
        </div>
      )}

      {!issuedKey && !issuingKey && (
        <button className="act ghost" disabled={busy} onClick={() => setIssuingKey(true)}>
          {t.issueKey}
        </button>
      )}

      {issuingKey && (
        <form onSubmit={(e) => void issueKey(e)}>
          <label className="field">
            <span>{t.keyName}</span>
            <input value={keyName} onChange={(e) => setKeyName(e.target.value)} autoFocus required />
          </label>
          <button className="act" type="submit" disabled={keyBusy}>
            {keyBusy ? t.working : t.issueKey}
          </button>
        </form>
      )}
    </div>
  )
}
